// src/pages/Admin/Groups/EditGroupModal.jsx
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  FaSave,
  FaTimes,
  FaTrash,
  FaUsers,
  FaBuilding,
  FaListOl,
  FaTag,
} from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateGroup, getGroupsHome } from "../../../services/groupServices";
import { getAllEnseignants } from "../../../services/adherentServices";
import { getDojos } from "../../../services/dojoServices";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Quill
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

// Palette couleurs
const colors = [
  { label: "Bleu", value: "bg-blue-400" },
  { label: "Vert", value: "bg-green-400" },
  { label: "Jaune", value: "bg-yellow-400" },
  { label: "Rouge", value: "bg-red-400" },
  { label: "Indigo", value: "bg-indigo-400" },
  { label: "Violet", value: "bg-purple-400" },
  { label: "Rose", value: "bg-pink-400" },
  { label: "Orange", value: "bg-orange-400" },
  { label: "Cyan", value: "bg-teal-400" },
  { label: "Marron", value: "bg-amber-600" },
  { label: "Turquoise", value: "bg-cyan-500" },
  { label: "Gris", value: "bg-gray-500" },
  { label: "Lime", value: "bg-lime-400" },
  { label: "Fuchsia", value: "bg-fuchsia-500" },
];

const EditGroupModal = ({ isActive, onClose, groupData, refreshGroups }) => {
  const dialogRef = useRef(null);

  const [formData, setFormData] = useState({
    id: groupData?.id,
    nom: groupData?.nom || "",
    discipline_groupe: groupData?.discipline_groupe || "",
    tarif_licence: groupData?.tarif_licence || "",
    tarif_cotisation: groupData?.tarif_cotisation || "",
    tarif_omnisport: groupData?.tarif_omnisport || "",
    tarif_comite: groupData?.tarif_comite || "",
    info_groupe: groupData?.info_groupe || "",
    is_active: !!groupData?.is_active,
    ordre_groupe: groupData?.ordre_groupe || "",
    quota_groupe: groupData?.quota_groupe || "",
    color_groupe: groupData?.color_groupe || colors[0].value,
    dojo_id: groupData?.dojo_id || "",
  });

  // Données liées
  const [dojos, setDojos] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [disciplinesSuggestions, setDisciplinesSuggestions] = useState([]);

  // Enseignants dynamiques
  const [enseignantsList, setEnseignantsList] = useState([]);

  // Horaires dynamiques
  const [horaires, setHoraires] = useState([{ jour: "", debut: "", fin: "" }]);

  // Infos (Quill)
  const [infoGroupe, setInfoGroupe] = useState(groupData?.info_groupe || "");

  // Tranches d’âge
  const [modeAge, setModeAge] = useState("annees"); // "annees" | "ageMin"
  const [anneesSelectionnees, setAnneesSelectionnees] = useState([]);
  const [ageMin, setAgeMin] = useState("");

  // Liste d'années
  const currentYear = new Date().getFullYear();
  const listeAnnees = Array.from({ length: 20 }, (_, i) => currentYear - i);

  // Ouverture : focus + ESC + lock du scroll
  useEffect(() => {
    if (!isActive) return;

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom']")?.focus();
    }, 0);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onEsc);

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onEsc);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Init données externes
  useEffect(() => {
    const fetchDojos = async () => {
      try {
        const data = await getDojos();
        setDojos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des dojos", error);
        toast.error("Erreur lors de la récupération des dojos");
      }
    };

    const fetchEnseignants = async () => {
      try {
        const data = await getAllEnseignants();
        setEnseignants(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des enseignants", error);
        toast.error("Erreur lors de la récupération des enseignants");
      }
    };

    const fetchDisciplines = async () => {
      try {
        const data = await getGroupsHome();
        const uniques = [
          ...new Set(
            (Array.isArray(data) ? data : [])
              .map((g) => String(g?.discipline_groupe || "").trim())
              .filter(Boolean),
          ),
        ].sort((a, b) => a.localeCompare(b, "fr"));

        setDisciplinesSuggestions(uniques);
      } catch (error) {
        console.error("Erreur lors de la récupération des disciplines", error);
      }
    };

    fetchDojos();
    fetchEnseignants();
    fetchDisciplines();
  }, []);

  // Hydrate à partir de groupData
  useEffect(() => {
    if (!groupData) return;

    // Reconstruire horaires (horaire1..6 -> [{jour,debut,fin}])
    const parsed = [];
    for (let i = 1; i <= 6; i++) {
      const value = groupData[`horaire${i}`];
      if (value) {
        const match = value.match(/(\w+)\s+(\d{2}:\d{2})-(\d{2}:\d{2})/);
        if (match) {
          parsed.push({
            jour: match[1],
            debut: match[2],
            fin: match[3],
          });
        }
      }
    }
    setHoraires(parsed.length ? parsed : [{ jour: "", debut: "", fin: "" }]);

    // Enseignants existants
    const ensInit = [];
    for (let i = 1; i <= 6; i++) {
      const e = groupData[`enseignant${i}`];
      if (e) ensInit.push(e);
    }
    setEnseignantsList(ensInit.length ? ensInit : [""]);

    // Âges / Années
    if (groupData.annees_naissance?.startsWith("+")) {
      setModeAge("ageMin");
      setAgeMin(groupData.annees_naissance.replace("+", "").replace(" ans", ""));
      setAnneesSelectionnees([]);
    } else {
      setModeAge("annees");
      setAnneesSelectionnees(
        groupData.annees_naissance
          ? groupData.annees_naissance.split("-").map((y) => parseInt(y, 10))
          : [],
      );
      setAgeMin("");
    }

    // Form data & Quill
    setFormData((prev) => ({
      ...prev,
      id: groupData.id,
      nom: groupData.nom || "",
      discipline_groupe: groupData.discipline_groupe || "",
      tarif_licence: groupData.tarif_licence || "",
      tarif_cotisation: groupData.tarif_cotisation || "",
      tarif_omnisport: groupData.tarif_omnisport || "",
      tarif_comite: groupData.tarif_comite || "",
      is_active: !!groupData.is_active,
      ordre_groupe: groupData.ordre_groupe || "",
      quota_groupe: groupData.quota_groupe || "",
      color_groupe: groupData.color_groupe || colors[0].value,
      dojo_id: groupData.dojo_id || "",
    }));

    setInfoGroupe(groupData.info_groupe || "");
  }, [groupData]);

  // Helpers
  const handleClose = () => {
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Horaires
  const addHoraire = () => {
    if (horaires.length < 6) {
      setHoraires((prev) => [...prev, { jour: "", debut: "", fin: "" }]);
    }
  };

  const handleHoraireChange = (index, field, value) => {
    const next = [...horaires];
    next[index][field] = value;
    setHoraires(next);
  };

  // Enseignants
  const addEnseignant = () => {
    if (enseignantsList.length < 6) {
      setEnseignantsList((prev) => [...prev, ""]);
    }
  };

  const handleEnseignantChange = (index, value) => {
    const next = [...enseignantsList];
    next[index] = value;
    setEnseignantsList(next);
  };

  // Couleur
  const handleColorChange = (color) => {
    setFormData((prev) => ({ ...prev, color_groupe: color.value }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.dojo_id) {
      toast.error("Veuillez sélectionner un dojo avant de valider.");
      return;
    }

    try {
      const horairesFormatted = horaires
        .filter((h) => h.jour && h.debut && h.fin)
        .map((h) => `${h.jour} ${h.debut}-${h.fin}`);

      let finalAnneesNaissance = "";
      if (modeAge === "annees") {
        finalAnneesNaissance = anneesSelectionnees.sort((a, b) => b - a).join("-");
      } else if (modeAge === "ageMin" && ageMin) {
        finalAnneesNaissance = `+${ageMin} ans`;
      }

      const fullFormData = {
        ...formData,
        discipline_groupe: String(formData.discipline_groupe || "").trim(),
        info_groupe: infoGroupe,
        enseignant1: enseignantsList[0] || "",
        enseignant2: enseignantsList[1] || "",
        enseignant3: enseignantsList[2] || "",
        enseignant4: enseignantsList[3] || "",
        enseignant5: enseignantsList[4] || "",
        enseignant6: enseignantsList[5] || "",
        horaire1: horairesFormatted[0] || "",
        horaire2: horairesFormatted[1] || "",
        horaire3: horairesFormatted[2] || "",
        horaire4: horairesFormatted[3] || "",
        horaire5: horairesFormatted[4] || "",
        horaire6: horairesFormatted[5] || "",
        annees_naissance: finalAnneesNaissance,
      };

      await updateGroup(fullFormData);
      toast.success("Groupe mis à jour avec succès");
      refreshGroups?.();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du groupe", error);
      toast.error("Erreur lors de la mise à jour du groupe");
    }
  };

  if (!isActive) return null;

  // --- PORTAL ---
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Éditer un groupe"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-4xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh] sm:max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky en dégradé */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 sm:px-6 flex items-center justify-between">
          <div>
            <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
              Éditer un Groupe
            </h3>
            <p className="text-white/90 text-xs sm:text-sm">
              Modifiez dojo, horaires, âges, discipline, enseignants, quotas & informations.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 hover:bg-white/25 transition text-white"
            aria-label="Fermer"
            type="button"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Corps scrollable + Footer sticky dans le même <form> */}
        <form onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Dojo + Nom + Discipline */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {/* Dojo */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Dojo <span className="text-pink-600">*</span>
              </label>
              <div className="mt-1 relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                  <FaBuilding />
                </span>
                <select
                  name="dojo_id"
                  value={formData.dojo_id}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2
                             text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">Sélectionner un Dojo</option>
                  {dojos.map((dojo) => (
                    <option key={dojo.id} value={dojo.id}>
                      {dojo.dojo_nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Nom */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Nom du groupe <span className="text-pink-600">*</span>
              </label>
              <div className="mt-1 relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                  <FaUsers />
                </span>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  placeholder="Ex : Baby Judo, Cadets, Adultes débutants…"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2
                             text-slate-900 dark:text-slate-100
                             placeholder-slate-400 dark:placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Discipline */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Discipline <span className="text-slate-400">(optionnel)</span>
              </label>
              <div className="mt-1 relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                  <FaTag />
                </span>
                <input
                  type="text"
                  name="discipline_groupe"
                  list="disciplines-groupe-list"
                  value={formData.discipline_groupe}
                  onChange={handleChange}
                  placeholder="Ex : Judo, Taïso, Jujitsu..."
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2
                             text-slate-900 dark:text-slate-100
                             placeholder-slate-400 dark:placeholder-slate-500
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <datalist id="disciplines-groupe-list">
                  {disciplinesSuggestions.map((discipline) => (
                    <option key={discipline} value={discipline} />
                  ))}
                </datalist>
              </div>
            </div>
          </div>

          {/* Horaires */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 sm:p-4 bg-white/60 dark:bg-slate-900/40">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Horaires (jusqu’à 6)
              </h4>
              <button
                type="button"
                onClick={addHoraire}
                className="rounded-xl bg-white text-slate-900 hover:bg-white/90 shadow px-3 py-2 transition ring-1 ring-slate-200 dark:ring-slate-700"
                disabled={horaires.length >= 6}
                title="Ajouter un horaire"
              >
                + Ajouter un horaire
              </button>
            </div>

            <div className="space-y-2">
              {horaires.map((h, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr_auto] gap-2 items-center"
                >
                  <select
                    value={h.jour}
                    onChange={(e) => handleHoraireChange(index, "jour", e.target.value)}
                    className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/80 dark:bg-slate-900/70
                               text-slate-900 dark:text-slate-100
                               focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    {["", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((j) => (
                      <option key={j || "vide"} value={j}>
                        {j || "Jour"}
                      </option>
                    ))}
                  </select>

                  <input
                    type="time"
                    value={h.debut}
                    onChange={(e) => handleHoraireChange(index, "debut", e.target.value)}
                    className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/80 dark:bg-slate-900/70
                               text-slate-900 dark:text-slate-100
                               focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />

                  <input
                    type="time"
                    value={h.fin}
                    onChange={(e) => handleHoraireChange(index, "fin", e.target.value)}
                    className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/80 dark:bg-slate-900/70
                               text-slate-900 dark:text-slate-100
                               focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />

                  {horaires.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setHoraires(horaires.filter((_, i) => i !== index))}
                      className="inline-flex items-center justify-center p-2 rounded-xl border
                                 border-slate-200 dark:border-slate-700 hover:bg-slate-50
                                 dark:hover:bg-slate-800/60 transition"
                      title="Supprimer cet horaire"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Ages / Années */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 sm:p-4 bg-white/60 dark:bg-slate-900/40">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Tranche d’âge / années de naissance
            </h4>

            <div className="flex flex-wrap gap-3 mb-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="modeAge"
                  value="annees"
                  checked={modeAge === "annees"}
                  onChange={() => setModeAge("annees")}
                />
                <span>Sélectionner des années</span>
              </label>

              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="modeAge"
                  value="ageMin"
                  checked={modeAge === "ageMin"}
                  onChange={() => setModeAge("ageMin")}
                />
                <span>Âge minimum</span>
              </label>
            </div>

            {modeAge === "annees" ? (
              <div className="max-h-48 overflow-y-auto pr-1 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {listeAnnees.map((an) => (
                  <label key={an} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={anneesSelectionnees.includes(an)}
                      onChange={(e) =>
                        setAnneesSelectionnees((prev) =>
                          e.target.checked ? [...prev, an] : prev.filter((y) => y !== an),
                        )
                      }
                    />
                    {an}
                  </label>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-3">
                <label className="text-sm text-slate-700 dark:text-slate-300 sm:text-right">
                  Âge minimum
                </label>
                <input
                  type="number"
                  min={1}
                  className="w-40 px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                  placeholder="Âge minimum"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Tarifs / Quota / Ordre */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { name: "tarif_licence", label: "Tarif licence (€)", placeholder: "Ex : 46", step: "0.01" },
              { name: "tarif_cotisation", label: "Tarif cotisation (€)", placeholder: "Ex : 200", step: "0.01" },
              { name: "tarif_omnisport", label: "Tarif omnisport (€)", placeholder: "Ex : 6", step: "0.01" },
              { name: "tarif_comite", label: "Tarif comité (€)", placeholder: "Ex : 3,50", step: "0.01" },
              { name: "quota_groupe", label: "Quota du groupe", placeholder: "Ex : 25", step: "1" },
            ].map((f) => (
              <div key={f.name} className="space-y-1">
                <label htmlFor={f.name} className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {f.label}
                </label>
                <input
                  id={f.name}
                  type="number"
                  name={f.name}
                  value={formData[f.name]}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                  inputMode="numeric"
                  min="0"
                  step={f.step}
                  className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                   bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100
                   placeholder-slate-400 dark:placeholder-slate-500
                   focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
            {/* Ordre */}
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Ordre d’affichage
              </label>
              <div className="mt-1 relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-white/90">
                  <FaListOl />
                </span>
                <input
                  type="number"
                  name="ordre_groupe"
                  value={formData.ordre_groupe}
                  onChange={handleChange}
                  placeholder="Ordre du groupe"
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700
                             bg-white/80 dark:bg-slate-900/70 pl-9 pr-3 py-2
                             text-slate-900 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
            </div>

            {/* Actif */}
            <div className="flex items-end">
              <label className="flex items-center gap-3 w-full rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/60 dark:bg-slate-900/40">
                <span className="text-sm text-slate-800 dark:text-slate-200">
                  Groupe actif ? <span className="opacity-75">(visible à l’inscription)</span>
                </span>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-5 w-5 accent-violet-500"
                />
              </label>
            </div>
          </div>

          {/* Infos complémentaires */}
          <div>
            <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Infos complémentaires du groupe
            </label>
            <div
              className="[&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                            [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                            [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                            [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
            >
              <ReactQuill
                value={infoGroupe}
                onChange={setInfoGroupe}
                theme="snow"
                placeholder="Informations supplémentaires sur le groupe"
                modules={modules}
                formats={formats}
              />
            </div>
          </div>

          {/* Enseignants */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 sm:p-4 bg-white/60 dark:bg-slate-900/40">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Enseignants (jusqu’à 6)
              </h4>
              <button
                type="button"
                onClick={addEnseignant}
                className="rounded-xl bg-white text-slate-900 hover:bg-white/90 shadow px-3 py-2 transition ring-1 ring-slate-200 dark:ring-slate-700"
                disabled={enseignantsList.length >= 6}
                title="Ajouter un enseignant"
              >
                + Ajouter un enseignant
              </button>
            </div>

            <div className="space-y-2">
              {enseignantsList.map((value, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={value}
                    onChange={(e) => handleEnseignantChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700
                               bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100
                               focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">Sélectionner Enseignant {index + 1}</option>
                    {enseignants.map((e) => (
                      <option key={e.id} value={`${e.nom} ${e.prenom}`}>
                        {e.nom} {e.prenom}
                      </option>
                    ))}
                  </select>

                  {enseignantsList.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEnseignantsList(enseignantsList.filter((_, i) => i !== index))
                      }
                      className="inline-flex items-center justify-center p-2 rounded-xl border
                                 border-slate-200 dark:border-slate-700 hover:bg-slate-50
                                 dark:hover:bg-slate-800/60 transition"
                      title="Supprimer cet enseignant"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Couleur */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/60 dark:bg-slate-900/40">
            <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
              Couleur du groupe
            </span>
            <div className="flex flex-wrap gap-2">
              {colors.map((color) => {
                const selected = formData.color_groupe === color.value;
                return (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-md border-2 transition transform ${color.value} ${
                      selected ? "border-black scale-110" : "border-white/50"
                    } ring-1 ring-slate-200 dark:ring-slate-700`}
                    title={color.label}
                    onClick={() => handleColorChange(color)}
                  />
                );
              })}
            </div>
          </div>

          {/* Footer sticky */}
          <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 mt-6">
            <div className="pointer-events-none h-6 bg-gradient-to-t from-white/95 dark:from-slate-900/95 to-transparent" />
            <div className="pointer-events-auto bg-white/95 dark:bg-slate-900/95 border-t border-slate-200/70 dark:border-slate-800/70 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 px-4 py-2 inline-flex items-center gap-2"
                title="Annuler"
              >
                <FaTimes />
                Annuler
              </button>
              <Button text="Enregistrer" icon={FaSave} type="submit" className="!rounded-xl gap-2" />
            </div>
          </div>
        </form>
      </div>

      {/* Styles locaux (placeholders + scrollbar) */}
      <style>{`
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .flex-1.min-h-0.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }

        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </div>,
    document.body,
  );
};

export default EditGroupModal;